import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, RendererFactory2, Renderer2, Optional } from '@angular/core';
import { CUSTOMERENVIRONMENT, WINDOW } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { AuthService } from '@app/services/auth.service';
import { FeatureSettingsService } from '@app/services/feature-settings.service';
import { UserService } from '@app/services/user.service';
import { BrowserAuthError, BrowserAuthErrorMessage, AuthenticationResult, RedirectRequest, PopupRequest } from '@azure/msal-browser';
import { Environment } from '@environments/interface.environment';
import { forkJoin, fromEvent, Observable, throwError, EMPTY } from 'rxjs';
import { catchError, first, map, tap, mergeMap, switchMap } from 'rxjs/operators';
import { GITVERSION } from '@environments/git-version';
import { CacheService } from '@app/services/cache.service';
import { MsteamsService } from '@app/services/msteams.service';

@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  private renderer: Renderer2;

  private readonly CacheKeyPopupsAreAllowed = 'popups-allowed';
  private readonly PopupsAreAllowed = true;

  constructor(
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthService,
    private userService: UserService,
    private cacheService: CacheService,
    private featureSettingsService: FeatureSettingsService,
    private rendererFactory: RendererFactory2,
    @Optional() private msteamsService?: MsteamsService,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.PopupsAreAllowed = this.cacheService.getWithCustomKey(this.CacheKeyPopupsAreAllowed, true);
  }

  /**
   * This should only be called from the main workplace app module to hook into
   * the init process and hold off or continue the bootstrap depending on authentication status.
   * */
  public init(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let initObservable$: Observable<void>;

      if (Utilities.inTeamsAuthPopupRedirect(this.window)) {
        console.log('MS Teams handling when we have opened the authenticate popup using MS Teams API');
        // MS Teams handling when we have opened the authenticate popup using MS Teams API
        initObservable$ = this.authService.handleRedirect()
          .pipe(
            switchMap(authenticatedResult => {
              if (authenticatedResult) {
                if (this.msteamsService) {
                  return this.msteamsService.initMsTeams()
                    .pipe(
                      tap(() => {
                        this.msteamsService.notifyAuthSuccess(JSON.stringify(authenticatedResult));
                        reject('Window will be closed by MS Teams API');
                      }));
                }
              }
              return EMPTY;
            }));
        resolve(initObservable$.toPromise());
      } else {
        // MS Teams handling - Try SSO Silent first. If we dont succeed we go ahead and open a popup for our custom 'msteams-auth' route using the MS Teams API
        if (Utilities.isInTeamsApp(this.window) && this.msteamsService) {

          initObservable$ = this.msteamsService.initMsTeams()
            .pipe(
              mergeMap(() => {

                return this.authService.ssoSilent(this.authService.loginRequest);
              }),
              mergeMap((authenticatedResult) => {

                this.authService.accountInfo = authenticatedResult.account;
                return this.initUserAndFeatures();
              }),
              catchError((err) => {

                if (err?.errorCode === BrowserAuthErrorMessage.silentPromptValueError.code) {

                  const teamsObs$ = this.msteamsService.authenticate()
                    .pipe(
                      mergeMap((authenticatedResult: string) => {

                        const result = JSON.parse(authenticatedResult) as AuthenticationResult;
                        this.authService.accountInfo = result.account;
                        return this.initUserAndFeatures();
                      }),
                      catchError((error) => {
                        if (this.authService.accountInfo) {
                          return this.initUserAndFeatures();
                        }
                      }));

                  return this.createSignInButtonAndListenOnClick(teamsObs$, false);
                } else if (err.errorCode === 'login_required') {
                    if (this.authService.accountInfo) {
                      return this.initUserAndFeatures();
                    }
                } else {

                  return throwError(err);
                }
              }));
        } else {

          initObservable$ = this.login(reject);
        }

        resolve(initObservable$.toPromise());
      }
    });
  }

  private login(reject: (reason?: any) => void) {

    return this.authService.handleRedirect()
      .pipe(
        switchMap((authenticatedResult) => {

          // The handleredirect will always initialize since we use redirect flow as a fallback.
          // If result is null we're are not authenticated and will need to start a login flow
          // If we have a result we're coming back from an authentication process (using redirect) and we can continue initializing the user and featuresettings.
          if (authenticatedResult) {

            this.authService.useRedirect = true;
            return this.initUserAndFeatures();
          } else {
            if ((this.environment.featureSettings.coreIntranetEnabled && Utilities.isInIframe(this.window)) || Utilities.isInIframe(this.window)) {
              // This flow is used by Core Intranet or when Iframed - in the Core intra case WP is always iframed inside SharePoint so we dont care about redirect-flow.
              return this.authService.ssoSilent(this.authService.loginRequest)
                .pipe(
                  mergeMap((authResult) => {
                    this.authService.accountInfo = authResult.account;
                    return this.initUserAndFeatures();
                  }),
                  catchError((err) => {
                    if (err?.errorCode === BrowserAuthErrorMessage.silentPromptValueError.code) {
                      return this.loginPopup();
                    } else {
                      return throwError(err);
                    }
                  }),
                  catchError((err) => {
                    if (err instanceof BrowserAuthError) {
                      // Fallback to create a sign in button if popups are blocked
                      if (err.errorCode === BrowserAuthErrorMessage.popupWindowError.code) {
                        this.cacheService.setWithCustomKey(this.CacheKeyPopupsAreAllowed, false);
                        return this.createSignInButtonAndListenOnClick();
                      }
                    }
                    return throwError(err);
                  }));
            } else {
              if (this.authService.useRedirect || !this.PopupsAreAllowed) {
                return this.loginRedirect(reject);
              } else {
                // Setup the default login flow with fallback to redirect
                const loginPopupWithFallBackToRedirect$ = this.loginPopup()
                  .pipe(
                    catchError((err) => {
                      if (err instanceof BrowserAuthError) {
                        // Fallback to redirect if popup fails due to blocking setting in browser
                        if (err.errorCode === BrowserAuthErrorMessage.popupWindowError.code) {
                          this.cacheService.setWithCustomKey(this.CacheKeyPopupsAreAllowed, false);
                          return this.loginRedirect(reject);
                        }
                      }
                      return throwError(err);
                    })
                  );

                return loginPopupWithFallBackToRedirect$;
              }
            }
          }
        }),
        catchError((err) => {
          if (err instanceof BrowserAuthError) {
            if (err.errorCode === BrowserAuthErrorMessage.interactionInProgress.code) {
              reject('Do not continue with app initialization - Interaction in progress');
            }
          }
          return throwError(err);
        })
      );
  }

  private loginPopup(): Observable<void> {
    return this.authService.loginPopup((this.authService.loginRequest as PopupRequest))
      .pipe(
        mergeMap(() => {
          return this.initUserAndFeatures();
        }));
  }

  private loginRedirect(reject: (reason?: any) => void): Observable<void> {
    return this.authService.loginRedirect((this.authService.loginRequest as RedirectRequest))
      .pipe(
        tap(() => {
          reject('Do not continue with app initialization - Login redirect will start now');
        }),
        catchError((err) => {
          if (err instanceof BrowserAuthError) {
            // Create sign-in button if redirect happens in iframe - We will HAVE to use popup flow and the user needs to click the button manually.
            if (err.errorCode === BrowserAuthErrorMessage.redirectInIframeError.code) {

              return this.createSignInButtonAndListenOnClick();
            }
            if (err.errorCode === BrowserAuthErrorMessage.interactionInProgress.code) {
              reject('Do not continue with app initialization - Interaction in progress');
            }
          }
          return throwError(err);
        }));
  }

  private createSignInButtonAndListenOnClick(onClickAction?: Observable<void>, addPopupNotice = true): Observable<void> {

    const div = this.renderer.createElement('div');
    const button = this.renderer.createElement('button');
    const paragraph = this.renderer.createElement('p');
    const img = this.renderer.createElement('img');

    this.renderer.addClass(div, 'absolute-center-position');

    ['btn', 'btn-primary', 'btn-lg', 'd-block', 'mx-auto'].forEach(cssClass => {
      this.renderer.addClass(button, cssClass);
    });

    ['text-center', 'mt-3'].forEach(cssClass => {
      this.renderer.addClass(paragraph, cssClass);
    });

    ['d-block', 'mx-auto', 'img-thumbnail', 'w-50'].forEach(cssClass => {
      this.renderer.addClass(img, cssClass);
    });

    this.renderer.setAttribute(img, 'src', `assets/popup_block.png?v=${GITVERSION.hash}`);

    const paragraphText = this.renderer.createText($localize`:@@allow-popups:You can skip this step by allowing popups for this site in your browser (see below).`);
    const buttonText = this.renderer.createText($localize`:@@click-to-sign-in:Click to Sign in`);

    this.renderer.appendChild(paragraph, paragraphText);
    this.renderer.appendChild(button, buttonText);
    this.renderer.appendChild(div, button);
    if (addPopupNotice) {
      this.renderer.appendChild(div, paragraph);
      this.renderer.appendChild(div, img);
    }

    this.renderer.appendChild(this.document.body, div);

    return fromEvent(button, 'click')
      .pipe(
        first(), // only listen to the frist click otherwise this observable chain will never complete and the init process will halt.
        tap(() => {
          button.remove();
          paragraph.remove();
          img.remove();
          div.remove();
        }),
        mergeMap(() => {
          if (onClickAction) {
            return onClickAction;
          } else {
            return this.loginPopup();
          }
        })
      );
  }

  private initUserAndFeatures(): Observable<void> {
    return forkJoin([
      this.userService.getOrCreateMe(),
      this.featureSettingsService.getFeatureSettings()
    ])
      .pipe(map(() => {
        // We have to return void here so no actual result.
      }));
  }
}
